import React, { useState, useRef, useEffect, useCallback } from 'react';
import './ActionMenu.css';
import { createPortal } from 'react-dom';

/**
 * Portal-based action menu that renders outside scrollable containers.
 * Bypasses Popper.js to avoid positioning issues inside table-responsive wrappers.
 *
 * @param {React.ReactNode} trigger - The toggle button (defaults to a three-dot icon)
 * @param {React.ReactNode} children - Menu items (<ActionMenu.Item> or <ActionMenu.Divider>)
 * @param {string} className - Extra class names on the wrapper <div>
 * @param {string} toggleClassName - Class names for the toggle <button>. Pass ''
 *        to drop the default ghost styling and use e.g. real `btn btn-outline-
 *        secondary btn-sm` classes so the trigger matches sibling toolbar buttons.
 * @param {string} ariaLabel - Accessible label for the toggle button.
 */
const ActionMenu = ({ trigger, children, className = '', toggleClassName, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback((e) => {
    e.stopPropagation();
    setOpen((prev) => {
      if (!prev && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.right });
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        close();
      }
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') close(); };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || !menuRef.current) return;
    const menu = menuRef.current;
    // rect is the menu's *visual* box — it already reflects the CSS
    // translateX(-100%) that right-aligns the menu to `left`. So rect.right ≈
    // pos.left (the button's right edge) and rect.left ≈ pos.left - width.
    const rect = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let { top, left } = pos;

    // Horizontal: nudge only if a visual edge is actually off-screen. (The old
    // `left > vw - width` test subtracted the width a second time on top of the
    // CSS transform, throwing the menu a full menu-width to the left whenever
    // the button sat near the right edge.)
    if (rect.right > vw - 4) left -= rect.right - (vw - 4);
    if (rect.left < 4) left += 4 - rect.left;
    // Vertical: flip above the button if it would overflow the bottom.
    if (top + rect.height > vh - 8) top = Math.max(4, top - rect.height - (btnRef.current?.offsetHeight || 0) - 8);

    if (top !== pos.top || left !== pos.left) setPos({ top, left });
  });

  return (
    <div className={`action-menu-wrap ${className}`} style={{ display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        className={toggleClassName === undefined ? 'action-menu-toggle' : toggleClassName}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={ariaLabel}
      >
        {trigger || <ThreeDots />}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="action-menu-portal"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1070 }}
          role="menu"
        >
          {React.Children.map(children, (child) => {
            if (!child) return null;
            if (child.type === ActionMenu.Divider) return child;
            return React.cloneElement(child, {
              onClick: (e) => {
                close();
                child.props.onClick?.(e);
              },
            });
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const ThreeDots = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 192 512">
    <path d="M96 184c39.8 0 72 32.2 72 72s-32.2 72-72 72-72-32.2-72-72 32.2-72 72-72zM24 80c0 39.8 32.2 72 72 72s72-32.2 72-72S135.8 8 96 8 24 40.2 24 80zm0 352c0 39.8 32.2 72 72 72s72-32.2 72-72-32.2-72-72-72-72 32.2-72 72z"/>
  </svg>
);

ActionMenu.Item = ({ children, onClick, className = '', ...rest }) => (
  <button
    type="button"
    className={`action-menu-item ${className}`}
    role="menuitem"
    onClick={onClick}
    {...rest}
  >
    {children}
  </button>
);
ActionMenu.Item.displayName = 'ActionMenu.Item';

ActionMenu.Divider = () => <hr className="action-menu-divider" />;
ActionMenu.Divider.displayName = 'ActionMenu.Divider';

export default ActionMenu;
